import React, { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX, Play } from "lucide-react";

const VSL_URL =
  "https://customer-assets.emergentagent.com/job_tower-crane-pro/artifacts/nead97c5_1%20CAPA.MOV";

export default function VSLSection() {
  const videoRef = useRef(null);
  const sectionRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [started, setStarted] = useState(false);

  // Autoplay when section enters viewport; pause when leaves.
  useEffect(() => {
    const v = videoRef.current;
    const s = sectionRef.current;
    if (!v || !s) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const p = v.play();
            if (p && typeof p.catch === "function") p.catch(() => {});
            setStarted(true);
          } else {
            v.pause();
          }
        });
      },
      { threshold: 0.4 }
    );
    io.observe(s);
    return () => io.disconnect();
  }, []);

  const toggleMute = async () => {
    const v = videoRef.current;
    if (!v) return;
    const newMuted = !v.muted;
    v.muted = newMuted;
    setMuted(newMuted);
    if (!newMuted) {
      v.currentTime = 0;
      try { await v.play(); } catch (e) { /* noop */ }
    }
  };

  const manualPlay = async () => {
    const v = videoRef.current;
    if (!v) return;
    try { v.muted = false; setMuted(false); await v.play(); setStarted(true); } catch (e) {}
  };

  return (
    <section
      ref={sectionRef}
      className="relative py-24 sm:py-32 bg-zinc-950 border-b border-white/10"
      data-testid="vsl-section"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-yellow-400 mb-3">
            <span className="w-2 h-2 bg-yellow-400 pulse-dot" />
            Mensagem do Kawan
          </div>
          <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.9]">
            UMA PALAVRA <br />
            <span className="text-yellow-400">DIRETA AO PONTO.</span>
          </h2>
        </div>

        <div className="relative aspect-video bg-black border border-yellow-400/30 shadow-[0_30px_80px_-20px_rgba(253,224,71,0.3)] overflow-hidden">
          <video
            ref={videoRef}
            src={VSL_URL}
            className="absolute inset-0 w-full h-full object-cover"
            playsInline
            muted={muted}
            loop
            preload="auto"
            data-testid="vsl-inline-video"
          />

          {/* Unmute click-layer */}
          {muted && started && (
            <button
              onClick={toggleMute}
              data-testid="vsl-inline-unmute"
              aria-label="Ativar som"
              className="absolute inset-0 flex items-center justify-center group cursor-pointer"
            >
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/45 transition-colors" />
              <div className="relative flex flex-col items-center gap-3">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-yellow-400 rounded-full flex items-center justify-center shadow-[0_15px_40px_-10px_rgba(253,224,71,0.6)] group-hover:scale-110 transition-transform">
                  <VolumeX className="w-7 h-7 sm:w-9 sm:h-9 text-black" />
                </div>
                <span className="text-xs sm:text-sm uppercase tracking-widest text-white font-semibold">
                  Toca para ativar o som
                </span>
              </div>
            </button>
          )}

          {!started && (
            <button
              onClick={manualPlay}
              data-testid="vsl-inline-play"
              aria-label="Reproduzir"
              className="absolute inset-0 flex items-center justify-center group cursor-pointer"
            >
              <div className="absolute inset-0 bg-black/60" />
              <div className="relative w-20 h-20 bg-yellow-400 rounded-full flex items-center justify-center shadow-[0_15px_40px_-10px_rgba(253,224,71,0.6)] group-hover:scale-110 transition-transform">
                <Play className="w-9 h-9 text-black fill-black ml-1" />
              </div>
            </button>
          )}

          {!muted && (
            <button
              onClick={toggleMute}
              data-testid="vsl-inline-mute"
              className="absolute bottom-3 right-3 inline-flex items-center gap-2 bg-black/60 backdrop-blur-sm border border-white/10 text-white text-xs uppercase tracking-widest px-3 py-2 hover:bg-black/80 transition-colors"
            >
              <Volume2 className="w-4 h-4 text-yellow-400" />
              Som
            </button>
          )}
        </div>

        <p className="mt-6 text-center text-sm text-zinc-500 uppercase tracking-widest">
          Vídeo com som · Sobe o volume do teu telemóvel
        </p>
      </div>
    </section>
  );
}
